import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { queryOptions } from '~/shared/constants';
import { Menu, MenuProps } from 'antd';
import { fetchNavHirarchy } from '~/shared/services/api';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import { ReactComponent as Apps } from '~/shared/assets/apps.svg';
import { ReactComponent as Commodities } from '~/shared/assets/commodities.svg';
import { ReactComponent as Cripto } from '~/shared/assets/cripto.svg';
import { ReactComponent as Dashboards } from '~/shared/assets/dashboards.svg';
import { ReactComponent as EquityFundamentals } from '~/shared/assets/equity-fundamentals.svg';
import { ReactComponent as Geopolitics } from '~/shared/assets/geopolitics.svg';
import { ReactComponent as InternalInformation } from '~/shared/assets/internal-information.svg';
import { ReactComponent as QuantifiedNarratives } from '~/shared/assets/quantified-narratives.svg';
import { ReactComponent as RiskAndAnalitics } from '~/shared/assets/risk-and-analitics.svg';
import { ReactComponent as SocialInsight } from '~/shared/assets/social-insight.svg';
import { ReactComponent as Bitcoin } from '~/shared/assets/bitcoin.svg';
import { ReactComponent as MenuArrow } from '~/shared/assets/menu_arrow.svg';
import { TextWithTooltip } from '~/shared/components/TextWithTooltip';
import {
  ArrowCircleUp,
  CallMerge,
  ChangeCircle,
  Cached,
  FindReplace,
  Logout,
  ShowChart,
  ImageAspectRatio,
  GroupWork,
  InsertChart,
  Insights,
  LibraryBooks,
  LegendToggle,
  LensBlur,
  LeakAdd,
  Mediation,
  Moving,
  MultilineChart,
  Nat,
  NetworkCheck,
  Outbound,
  OnlinePrediction,
  OpenWith,
  Public,
  Radar,
  RadioButtonChecked,
  SavedSearch,
  SettingsInputAntenna,
  SettingsBackupRestore,
  SyncAlt,
  SwapHoriz,
  WaterfallChart,
  Workspaces,
  DeviceHub,
  Grain,
  AttachMoney,
  AccountBalance,
  AddShoppingCart,
  Remove,
  AssuredWorkload,
} from '@mui/icons-material';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import 'antd/dist/reset.css';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const iconsMap: { [key: string]: any } = {
  Stream: <Apps />,
  LocalGasStation: <Commodities />,
  Bitcoin: <Cripto />,
  Widgets: <Dashboards />,
  LineStyle: <EquityFundamentals />,
  Language: <Geopolitics />,
  QueryStats: <InternalInformation />,
  BarChart: <QuantifiedNarratives />,
  Shield: <RiskAndAnalitics />,
  Reddit: <SocialInsight />,
  ArrowCircleUp: <ArrowCircleUp />,
  CallMerge: <CallMerge />,
  ChangeCircle: <ChangeCircle />,
  Cached: <Cached />,
  FindReplace: <FindReplace />,
  Logout: <Logout />,
  ShowChart: <ShowChart />,
  ImageAspectRatio: <ImageAspectRatio />,
  GroupWork: <GroupWork />,
  InsertChart: <InsertChart />,
  Insights: <Insights />,
  LibraryBooks: <LibraryBooks />,
  LegendToggle: <LegendToggle />,
  LensBlur: <LensBlur />,
  LeakAdd: <LeakAdd />,
  Mediation: <Mediation />,
  Moving: <Moving />,
  MultilineChart: <MultilineChart />,
  Nat: <Nat />,
  NetworkCheck: <NetworkCheck />,
  Outbound: <Outbound />,
  OnlinePrediction: <OnlinePrediction />,
  OpenWith: <OpenWith />,
  Public: <Public />,
  Radar: <Radar />,
  RadioButtonChecked: <RadioButtonChecked />,
  SavedSearch: <SavedSearch />,
  SettingsInputAntenna: <SettingsInputAntenna />,
  SettingsBackupRestore: <SettingsBackupRestore />,
  SyncAlt: <SyncAlt />,
  SwapHoriz: <SwapHoriz />,
  WaterfallChart: <WaterfallChart />,
  Workspaces: <Workspaces />,
  DeviceHub: <DeviceHub />,
  Grain: <Grain />,
  AttachMoney: <AttachMoney />,
  AccountBalance: <AccountBalance />,
  AddShoppingCart: <AddShoppingCart />,
  Remove: <Remove />,
  AssuredWorkload: <AssuredWorkload />,
};

const Container = styled.div`
  .ant-menu {
    background: transparent !important;
    font-family: 'Montserrat', sans-serif !important;
    font-size: 16px !important;
  }
  .ant-menu-item-selected {
    background: var(--menu-background-hover) !important;
    border-radius: 0 !important;
  }
  .ant-menu-item {
    .ant-menu-title-content {
      font-family: 'Montserrat', sans-serif !important;
      font-size: 16px !important;
      min-width: 180px !important;
      max-height: 39px !important;
      margin-left: -28px !important;
      padding-left: 0px !important;
    }
    height: 40px !important;
    line-height: 40px !important;
    width: 100% !important;
    color: var(--menu-text-color);
    fill: var(--menu-text-color) !important;
    margin: 0 !important;
    padding: 16px 8px 16px 96px !important;
    border-radius: 0px !important;
    &:hover {
      background: var(--menu-background-hover);
    }
  }

  .ant-menu-item-active {
    border-radius: 0 !important;
    background: var(--menu-background-hover) !important;
  }
  .ant-menu-title-content {
    max-height: 59px;
    font-family: 'Montserrat', sans-serif !important;
    font-size: 16px !important;
    font-weight: 500;
    max-width: calc(100% - 32px) !important;
    color: var(--menu-text-color);
    fill: var(--menu-text-color) !important;
    margin: 0 !important;
    padding-left: 10px;
    border-radius: 0 !important;
  }
  .ant-menu-item-group-title {
    display: none;
    color: var(--menu-text-color);
    fill: var(--menu-text-color) !important;
    padding-inline-start: 25px !important;
    &:hover {
      background: transparent !important;
      border-radius: 0 !important;
    }
  }
  .ant-menu-sub {
    .ant-menu-item-group-title {
      font-family: 'Montserrat', sans-serif !important;
      font-size: 16px !important;
      display: block;
      color: var(--menu-text-color);
      color: #bdbdbd;
      fill: var(--menu-text-color) !important;
      padding-inline-start: 52px !important;
      pointer-events: none;
      &:hover {
        background: transparent !important;
        border-radius: 0 !important;
      }
    }
  }
  .ant-menu-item-icon {
    max-width: 18px !important;
    max-height: 59px !important;
    min-width: 18px !important;
    min-height: 59px !important;
    padding: 21px 0 !important;
    fill: var(--menu-text-color) !important;
    border-bottom: 1px solid var(--menu-seperator-color);
  }
  .ant-menu-submenu-arrow:before,
  .ant-menu-submenu-arrow:after {
    background-color: var(--menu-text-color) !important;
  }
  .ant-menu-submenu-title {
    height: 59px !important;
    line-height: 59px !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 16px 8px 16px 24px !important;
    border-radius: 0 !important;
    &:hover {
      background: var(--menu-background-hover) !important;
      border-radius: 0 !important;
    }
  }
  .ant-menu-submenu-title > .ant-menu-title-content {
    border-bottom: 1px solid var(--menu-seperator-color);
  }
  .ant-menu-submenu-open > .ant-menu-submenu-title > .ant-menu-title-content {
    border-bottom: none;
  }
  .ant-menu-submenu-open > .ant-menu-submenu-title > .ant-menu-item-icon {
    border-bottom: none;
  }

  .ant-menu-submenu-open > .ant-menu-submenu-title > .ant-menu-submenu-expand-icon {
    transform: rotate(180deg);
    margin-top: -2px !important;
  }
  .ant-menu-submenu-open > .ant-menu-sub > .ant-menu-item:last-of-type > .ant-menu-title-content {
    border-bottom: 1px solid var(--menu-seperator-color);
    min-width: 220px !important;
    max-height: 39px;
    margin-left: -70px !important;
    padding-left: 42px !important;
  }
  .ant-menu-submenu-open
    > .ant-menu-sub
    > .ant-menu-item-group:last-of-type
    > .ant-menu-item-group-list
    > .ant-menu-item:last-of-type
    > .ant-menu-title-content {
    border-bottom: 1px solid var(--menu-seperator-color);
    min-width: 220px !important;
    max-height: 39px;
    margin-left: -70px !important;
    padding-left: 42px !important;
  }

  .ant-tooltip-inner {
    color: yellow !important;
    background-color: green !important;
    width: 800px;
  }
`;

type Group = {
  name: string;
  prisms: { id: string; name: string }[];
  items: { id: string; name: string }[];
  type?: string;
  icon?: string;
  id?: string;
  menuType?: string;
};

export default function DrawerContent() {
  const navigate = useNavigate();
  const { data, error }: any = useQuery(fetchNavHirarchy.key, fetchNavHirarchy, queryOptions);
  const routes = data?.data;
  const [, setSearchParams] = useSearchParams();

  const [, setSelectedCategoryDisplay] = useDynamicRecoil('selectedCategoryDisplay', undefined);
  const [, setSelectedChartDisplay] = useDynamicRecoil('selectedChartDisplay', undefined);
  const [, setSelectedButtonResolution] = useDynamicRecoil('dateFilterResolutionButton', undefined);
  const [, setSelectedResolution] = useDynamicRecoil('dateFilterResolution', undefined);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedChart, setSelectedChart] = useState('');

  const { pathname } = useLocation();

  const handleMenuClick = useCallback(
    (info: any) => {
      const keySegments = info.key.split('/');
      const categorySegment = keySegments[2] || '';
      const is4thLevel = keySegments[4];
      const chartSegment = keySegments[is4thLevel ? 4 : 3] || '';
      const nodeProps = info.node?.props ?? {};
      const dataResolution = nodeProps['data-resolution'];
      setSelectedResolution(dataResolution);
      setSelectedButtonResolution(dataResolution);
      setSelectedCategoryDisplay(categorySegment.replace(/%20/g, ' '));
      setSelectedCategory(categorySegment);
      setSelectedChart(chartSegment);
      setSearchParams({});
      navigate(info.key, { state: { fromDrawer: true } });
    },
    [navigate],
  );

  const handleMenuOpenClick = useCallback(
    (e: any) => {
      setSelectedCategory(
        e
          .filter(
            (val: string) =>
              val !== selectedCategory && val !== selectedCategory?.replace(/%20/g, ' ') && !val.includes('/'),
          )[0]
          ?.replace(/ /g, '%20'),
      );
    },
    [selectedCategory],
  );

  type MenuItem = Required<MenuProps>['items'][number];

  const getItem = (
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group',
  ): MenuItem => {
    return {
      key,
      icon,
      children,
      label,
      type,
    } as MenuItem;
  };

  const items: MenuProps['items'] = [];

  useEffect(() => {
    const pathOptions = pathname?.split('/');
    let subCategory = pathOptions?.[pathOptions.length - 3];
    if (pathOptions[1] === 'Internal') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Dashboards') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Geopolitics') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Social%20Insights') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'GS%20Marquee') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'GS%20Trial') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'GS%20SPG') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'GS%20STS') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'GS') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'State%20Street') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'MSCI') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Invesco') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'LSEG') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Deloitte') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Annexus') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Socgen') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'UOB') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Laurion') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Global%20Bank') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'State%20Street') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Global%20AM') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Global%20Index%20Provider') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'FIA%20Sponsor') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'European%20Bank') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Private%20Bank') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Hedge%20Fund') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Private%20Assets') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Rockhampton%20Trial') subCategory = pathOptions?.[2];
    if (pathOptions[2] === 'Apollo%20Trial') subCategory = pathOptions?.[2];

    const chart = pathOptions?.pop();
    setSelectedChart(chart || '');
    setSelectedCategory(subCategory || '');
    if (subCategory) setSelectedCategoryDisplay(subCategory.replace(/%20/g, ' '));
    setSelectedChartDisplay('');
  }, [pathname, routes]);

  const flattendRoutes: any =
    routes &&
    routes.reduce((arr: any[], curr: any) => arr.concat(curr.type === 'heatmap' ? curr.categories : curr), []);

  const isUserExpired =
    flattendRoutes &&
    flattendRoutes[flattendRoutes.length - 1]?.items[0].categories[
      flattendRoutes[flattendRoutes.length - 1]?.items[0].categories.length - 1
    ].name === 'UserExpired';

  if (flattendRoutes)
    flattendRoutes.forEach((r: Partial<Group>) => {
      const inItems = r.items && r.items.length > 0 ? r.items : r.prisms;
      if (r.menuType === 'SubMenu' && inItems) {
        const topItems = inItems.map((prism: any) => {
          const linkPrism = prism.name;
          const prismItems = prism.categories.map((item: any, itemIndex: number) => {
            const linkInPrism = item.name;
            const inPrismItems =
              item.categories &&
              item.categories.map((item: any, itemIndex: number) => {
                if (item.categories) return getItem(item.name, item.name + itemIndex);
                return getItem(item.name, isUserExpired ? '/' : `/${r.name}/${linkPrism}/${linkInPrism}/${item.id}`);
              });
            if (item.categories) return getItem(item.name, item.name + itemIndex, null, inPrismItems, 'group');
            return getItem(item.name, isUserExpired ? '/' : `/${r.name}/${linkPrism}/${item.id}`);
          });

          return getItem(
            prism.name,
            prism.name as string,
            prism.icon ? iconsMap[prism.icon] : undefined,
            prismItems,
            'group',
          );
        });
        items.push(getItem(r.name, r.name as string, r.icon ? iconsMap[r.icon] : undefined, topItems));
      }

      if (r.menuType !== 'SubMenu' && inItems) {
        inItems.forEach((prism: any) => {
          const linkPrism = prism.name;
          const prismItems = prism.categories.map((item: any, itemIndex: number) => {
            const linkInPrism = item.name;
            const inPrismItems =
              item.categories &&
              item.categories.map((item: any, itemIndex: number) => {
                if (item.categories) return getItem(item.name, item.name + itemIndex);
                return getItem(
                  <TextWithTooltip>{item.name}</TextWithTooltip>,
                  isUserExpired ? '/' : `/${r.name}/${linkPrism}/${linkInPrism}/${item.id}`,
                );
              });
            if (item.categories) return getItem(item.name, item.name + itemIndex, null, inPrismItems, 'group');
            return getItem(
              <TextWithTooltip>{item.name}</TextWithTooltip>,
              isUserExpired ? '/' : `/${r.name}/${linkPrism}/${item.id}`,
            );
          });
          items.push(
            getItem(prism.name, prism.name as string, prism.icon ? iconsMap[prism.icon] : undefined, prismItems),
          );
        });
      }
    });

  if (error) return <div>{error?.message}</div>;
  if (!routes) return null;

  if ((!selectedCategory || !selectedChart) && !pathname) return null;

  return (
    <Container>
      <Menu
        expandIcon={<MenuArrow />}
        overflowedIndicator={<Bitcoin />}
        mode="inline"
        theme="light"
        selectedKeys={
          (pathname && [pathname?.replace(/%20/g, ' ')]) ||
          (selectedChart && [selectedCategory?.replace(/%20/g, ' ') + '/' + selectedChart.replace(/%20/g, ' ')]) || ['']
        }
        openKeys={[
          selectedCategory,
          selectedCategory?.replace(/%20/g, ' '),
          pathname?.replace(/%20/g, ' ').replace('/', ''),
        ]}
        onClick={handleMenuClick}
        onOpenChange={handleMenuOpenClick}
        items={items}
      />
    </Container>
  );
}
